import { useAuth } from './auth.context';
import { useToast } from '../hooks/use-toast';

/**
 * Service for requesting more information about player injuries
 */
export const useInjuryRequestService = () => {
  const { getToken } = useAuth();
  const { toast } = useToast();
  
  /**
   * Request more information about a player's injury
   * 
   * @param playerId The player ID
   * @param playerName The player name
   * @param playerClub The player's club
   * @param injuryDetails The injury details
   * @param injuryDate The date of the injury
   * @returns Promise<boolean> indicating success or failure
   */
  const requestInjuryInfo = async (
    playerId: string,
    playerName: string,
    playerClub: string,
    injuryDetails: string,
    injuryDate: string
  ): Promise<boolean> => {
    try {
      const token = await getToken();
      if (!token) {
        toast({
          title: 'Error',
          description: 'You must be logged in to request injury information',
          variant: 'destructive',
        });
        return false;
      }

      // In development, use relative URLs to leverage the Vite proxy
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
      
      const response = await fetch(`${baseUrl}/api/v1/injury/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          playerId,
          playerName,
          playerClub,
          injuryDetails,
          injuryDate
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      toast({
        title: 'Request Submitted',
        description: data.message || 'Your request for more information about the player\'s injury has been submitted successfully.',
        duration: 3000,
      });
      
      return true;
    } catch (error) {
      console.error('Error requesting injury information:', error);
      
      toast({
        title: 'Request Failed',
        description: error instanceof Error ? error.message : 'Failed to submit your request. Please try again.',
        variant: 'destructive',
        duration: 3000,
      });
      
      return false;
    }
  };

  return { requestInjuryInfo };
};